<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">提现管理</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">审核用户提现申请</p>
      </div>
      <!-- 状态筛选 -->
      <div class="flex items-center gap-2">
        <label class="text-sm text-gray-500 dark:text-gray-400">状态筛选：</label>
        <select v-model="statusFilter" @change="currentPage = 1; loadRecords()"
          class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200">
          <option value="">全部</option>
          <option value="PENDING">待审核</option>
          <option value="APPROVED">已通过</option>
          <option value="REJECTED">已拒绝</option>
        </select>
      </div>
    </div>

    <!-- 列表 -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 overflow-x-auto">
      <table class="w-full text-sm text-left">
        <thead class="text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-700">
          <tr>
            <th class="px-4 py-3">ID</th>
            <th class="px-4 py-3">金额</th>
            <th class="px-4 py-3">提现方式</th>
            <th class="px-4 py-3">收款账号</th>
            <th class="px-4 py-3">账户名</th>
            <th class="px-4 py-3">状态</th>
            <th class="px-4 py-3">申请时间</th>
            <th class="px-4 py-3">审核备注</th>
            <th class="px-4 py-3">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="9" class="px-4 py-8 text-center text-gray-400 dark:text-gray-500">加载中...</td>
          </tr>
          <tr v-else-if="records.length === 0">
            <td colspan="9" class="px-4 py-8 text-center text-gray-400 dark:text-gray-500">暂无提现记录</td>
          </tr>
          <tr v-for="r in records" :key="r.id" class="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
            <td class="px-4 py-3 dark:text-gray-300">{{ r.id }}</td>
            <td class="px-4 py-3 font-medium dark:text-gray-200">¥{{ centsToYuan(r.amount) }}</td>
            <td class="px-4 py-3 dark:text-gray-300">{{ methodLabel(r.paymentMethod) }}</td>
            <td class="px-4 py-3 dark:text-gray-300">{{ r.paymentAccount }}</td>
            <td class="px-4 py-3 dark:text-gray-300">{{ r.accountName }}</td>
            <td class="px-4 py-3">
              <span :class="statusClass(r.status)" class="px-2 py-1 text-xs rounded-full">
                {{ statusLabel(r.status) }}
              </span>
            </td>
            <td class="px-4 py-3 text-gray-500 dark:text-gray-400">{{ formatDate(r.createdAt) }}</td>
            <td class="px-4 py-3 text-gray-500 dark:text-gray-400">{{ r.adminNote || '-' }}</td>
            <td class="px-4 py-3">
              <div v-if="r.status === 'PENDING'" class="flex gap-2">
                <button @click="handleApprove(r)"
                  class="link-btn-edit font-medium">
                  通过
                </button>
                <button @click="handleReject(r)"
                  class="link-btn-delete font-medium">
                  拒绝
                </button>
              </div>
              <span v-else class="text-gray-400 dark:text-gray-500 text-sm">-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分页 -->
    <Pagination v-if="total > 0"
      :total="total"
      v-model:current-page="currentPage"
      v-model:page-size="pageSize"
      :show-page-size-selector="false"
      :show-jumper="false"
      @update:current-page="loadRecords"
      @update:page-size="loadRecords"
    />

    <!-- 拒绝 Modal -->
    <Modal :show="showRejectModal" @close="closeRejectModal" title="拒绝提现申请">
      <WithdrawalRejectModal
        @submit="submitReject"
        @cancel="closeRejectModal"
      />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import Modal from '@/components/common/Modal.vue'
import WithdrawalRejectModal from './components/WithdrawalRejectModal.vue'
import Pagination from '@/components/ui/Pagination.vue'
import { getWithdrawalsAPI, approveWithdrawalAPI, rejectWithdrawalAPI } from '@/api/referral/withdrawal'
import type { WithdrawalRecord } from '@/types/referral/withdrawal'
import toaster from '@/utils/toaster'

const PAGE_SIZE = 20

const records = ref<WithdrawalRecord[]>([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(PAGE_SIZE)
const loading = ref(false)
const statusFilter = ref('')

/** 当前操作的提现记录 */
const currentWithdrawal = ref<WithdrawalRecord | null>(null)
const showRejectModal = ref(false)

const centsToYuan = (cents: number) => (cents / 100).toFixed(2)
const formatDate = (dateStr: string) => new Date(dateStr).toLocaleString()

const methodLabel = (method: string) => {
  const map: Record<string, string> = { alipay: '支付宝', wechat: '微信', bank: '银行卡' }
  return map[method] || method
}

const statusLabel = (status: string) => {
  const map: Record<string, string> = { PENDING: '待审核', APPROVED: '已通过', REJECTED: '已拒绝' }
  return map[status] || status
}

const statusClass = (status: string) => {
  switch (status) {
    case 'APPROVED': return 'bg-green-100 text-green-700'
    case 'REJECTED': return 'bg-red-100 text-red-700'
    default: return 'bg-yellow-100 text-yellow-700'
  }
}

const loadRecords = async () => {
  loading.value = true
  try {
    const res = await getWithdrawalsAPI(currentPage.value, pageSize.value, statusFilter.value || undefined)
    if (res.status === 200 && res.data) {
      records.value = res.data.items
      total.value = res.data.total
    }
  } catch (error) {
    console.error('加载提现记录失败:', error)
  } finally {
    loading.value = false
  }
}

/** 审核通过 */
const handleApprove = async (r: WithdrawalRecord) => {
  if (!confirm(`确定通过提现申请 #${r.id}（¥${centsToYuan(r.amount)}）？`)) return
  try {
    await approveWithdrawalAPI(r.id)
    toaster.success('审核通过')
    await loadRecords()
  } catch (error: any) {
    toaster.error(error.message || '审核失败')
  }
}

/** 打开拒绝 Modal */
const handleReject = (r: WithdrawalRecord) => {
  currentWithdrawal.value = r
  showRejectModal.value = true
}

/** 提交拒绝 */
const submitReject = async (adminNote: string | null) => {
  if (!currentWithdrawal.value) return
  try {
    await rejectWithdrawalAPI(currentWithdrawal.value.id, { adminNote })
    toaster.success('已拒绝')
    closeRejectModal()
    await loadRecords()
  } catch (error: any) {
    toaster.error(error.message || '操作失败')
  }
}

const closeRejectModal = () => {
  showRejectModal.value = false
  currentWithdrawal.value = null
}

onMounted(() => loadRecords())
</script>
